import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Trash2, PenLine, Info, FileDown, UserPlus, Navigation } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@/components/lib/Pagination";
import { setStatus, setPerPage, setCurrentPage, setVoucherSearch, clearVoucherMessages, deletePointBasedOfferById, fetchPointBasedOffer } from "@/redux/slice/loyalty/PointBasedOfferSlice";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog, FoodItemsDialog } from "@/components/dialog/common-dialog";
import { toast } from "react-hot-toast";
import AddCustomer from "../AddCustomer";
import { Link } from "react-router-dom";
import ViewPointBasedReward from "./ViewPointBasedReward";
import { addPointBasedOfferHistoryApi } from "@/api/PointBasedOfferHistory";
import { fetchPointBasedOfferHistories } from "@/redux/slice/loyalty/PointBasedOfferHistorySlice";
import { fetchLoyaltyTemplates } from "@/redux/slice/loyalty/templeteSlice";
import { exportPointBasedTemplatePdf } from "@/lib/export-point-based-template-pdf";
import { printLoyaltyCard } from "@/api/new-api/loyalty/gift-card";
import useAuth from "@/hooks/useAuth";
import hasPermission from "@/hooks/hasPermission";

function PointBasedRewardTab() {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [itemsDialog, setItemsDialog] = useState(null);
  const { status, search, vouchers, perPage, currentPage, totalPages } = useSelector(
    (state) => state.loyalty.pointBasedOffer
  );
  const [ingredientsDialog, setIngredientsDialog] = useState(false);
  const closeIngredients = () => {
    setIngredientsDialog(false);
    setItemsDialog(null);
  };

  // Handlers
  const handleSearchChange = useCallback(
    (e) => {
      dispatch(setVoucherSearch(e.target.value));
      //Remove it, if want in same page search
      dispatch(setCurrentPage(1));
    },
    [dispatch]
  );

  // Delete 
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const closeDeleteDialog = () => { setDeleteDialog(false) };
  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required to delete.");
        return;
      }
      dispatch(clearVoucherMessages());
      const res = await dispatch(deletePointBasedOfferById(id));
      if (deletePointBasedOfferById.fulfilled.match(res)) {
        toast.success("Point based reward deleted successfully.");
        setDeleteDialog(false);
        dispatch(clearVoucherMessages());
      } else if (deletePointBasedOfferById.rejected.match(res)) {
        toast.error(res?.payload);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!.");
    } finally {
      closeDeleteDialog();
    }
  };

  // Add customer
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [selectedSendVoucherViaOptions, setSelectedSendVoucherViaOptions] = useState([]);
  const closeAddCustomer = () => {
    setIsAddCustomerModalOpen(false); 
    setItemsDialog(null)
  };
  // View customer
  const [isSingleVoucherModalOpen, setIsSingleVoucherModalOpen] = useState(false);
  const closeSingleVoucher = () => {
    setIsSingleVoucherModalOpen(false); 
    setItemsDialog(null)
  };

  const handlePrintExport = async (rowData) => {
    if (!rowData) return;

    try {
      const pointBaseCard = rowData?.point_base_card || rowData;
      const restaurantId = pointBaseCard?.restaurant_id || rowData?.restaurant_id;

      if (!restaurantId) {
        toast.error("Restaurant ID not found for export.");
        return;
      }

      const templateList = await dispatch(
        fetchLoyaltyTemplates({
          restaurant_id: restaurantId,
          type: "point",
          per_page: 10,
        }),
      ).unwrap();

      await exportPointBasedTemplatePdf({
        selectedPointBased: rowData,
        templates: templateList,
        fileName: `${pointBaseCard?.code || "point-based"}.pdf`,
      });

      toast.success("Point-based reward exported successfully.");
    } catch (error) {
      toast.error(error?.message || "Failed to export point-based reward PDF.");
    }
  };

  const { user } = useAuth();

  const handleAddCustomerConfirm = async (selected, context) => {
    const selectedRow = itemsDialog;
    const channels = selected.map((s) => s.toLowerCase()).filter(Boolean);
    const cardId = selectedRow?.id || selectedRow?.point_base_card_id || selectedRow?.point_base_card?.id;
    const customerId = context?.selectedUser?.[0]?.id;
    const restaurantId = user?.restaurant_id || selectedRow?.restaurant_id || selectedRow?.point_base_card?.restaurant_id;

    if (!cardId) {
      toast.error("Point-based reward ID not found.");
      return false;
    }

    const templateId = selectedRow?.template_id || selectedRow?.point_base_card?.template_id;
    const isNativePrint = (templateId == 4 || templateId == 8 || templateId == 16);

    if (isNativePrint && channels.includes("print")) {
      const nativePrintPayload = {
        restaurant_id: Number(restaurantId),
        customer_id: customerId ? Number(customerId) : null,
        channels: channels,
        card_type: "point-base-card",
      };
      const nativePrintRes = await printLoyaltyCard(restaurantId, cardId, "point-base-card", nativePrintPayload);
      if (nativePrintRes?.status === "success") {
        toast.success("Point-based reward native print request submitted.");
        setIsAddCustomerModalOpen(false);
        setSelectedSendVoucherViaOptions([]);

        const offerQuery = {
          page: currentPage,
          per_page: perPage,
          search,
          restaurant_id: user?.restaurant_id || restaurantId,
        };
        if (status && status !== "0") {
          offerQuery.status = status;
        }
        dispatch(fetchPointBasedOffer(offerQuery));

        const historyQuery = {
          page: 1,
          per_page: 10,
          restaurant_id: user?.restaurant_id || restaurantId,
        };
        dispatch(fetchPointBasedOfferHistories(historyQuery));
      } else {
        toast.error(nativePrintRes?.message || "Failed to submit native print request.");
      }
      return true;
    }

    if (selected?.includes("Print")) {
      await handlePrintExport(selectedRow);
    }
  };

  return (
    <TabsContent value="pointBasedReward">
      <div className="mt-5">
        <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
          <div className="flex gap-4">
            <div className="relative">
              <Search
                size={18}
                className="text-cwhite3 absolute top-2 left-2"
              />
              <Input
                ref={inputRef}
                value={search}
                onChange={handleSearchChange}
                placeholder="Search"
                className="bg-cblack3 pl-8"
              />
            </div>
            {/* <div className="relative">
              <Select>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Template Name" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
            <div className="relative">
              <Select
                value={status}
                onValueChange={(value) => {
                dispatch(setStatus(value === "0" ? "" : value))
                dispatch(setCurrentPage(1))}}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col  gap-1.5 justify-end items-end">
            <div className="flex gap-3 justify-center items-center">
              <Button
                className={"bg-cblack3 border border-gray-600 text-cwhite2"}
              >
                <FileDown /> Export
              </Button>
              <div className="flex gap-1 justify-center items-center">
                <h3 className="text-white">Show</h3>
                <Select
                  value={String(perPage)}
                  onValueChange={(value) => {
                    dispatch(setPerPage(Number(value)))
                    dispatch(setCurrentPage(1))
                  }}
                >
                  <SelectTrigger className="w-[60px]">
                    <SelectValue placeholder={String(perPage)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        {/* table start */}
        <Table className="w-full mt-5">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Minimum Required Points</TableHead>
              <TableHead>Eligible Reward Points</TableHead>
              <TableHead>Reward</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Creation Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vouchers && vouchers?.length > 0 ? (
              vouchers?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.code || "N/A"}
                  </TableCell>
                  <TableCell>{item?.minimum_required_points}</TableCell>
                  <TableCell>{item?.eligible_reward_points}</TableCell>
                  <TableCell>
                    {item.type === 'food_item' ? (
                      <span className="flex items-center gap-1 cursor-pointer">
                        Items
                        <Eye
                          size={20}
                          onClick={() => {
                            setIngredientsDialog(true);
                            setItemsDialog(item);
                          }}
                        />
                      </span>
                    ) : (
                      <>
                        {item.type === 'fixed' && '£'}
                        {item?.reward}
                        {item.type === 'percentage' && '%'}
                      </>
                    )}
                  </TableCell>
                  <TableCell>{item?.note}</TableCell>
                  <TableCell>{new Date(item?.created_at).toLocaleDateString("en-GB")}</TableCell>
                  <TableCell>
                    <span
                      className={`text-base capitalize ${
                        item?.status === "active"
                          ? "text-cgreen"
                          : item?.status === "inactive"
                          ? "text-cred"
                          : "text-white"
                      }`}
                    >
                      {item?.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right justify-end flex gap-2 items-center">
                    <div className="flex items-center gap-2 h-8">
                      <Eye
                        onClick={() => {
                          setIsSingleVoucherModalOpen(true)
                          setItemsDialog(item);
                        }}
                        className="text-cwhite3 cursor-pointer"
                        size={20}
                      />
                      {hasPermission("loyalty_offer.point_based_offer.update_point_offer") && (
                      <span className="cursor-pointer">
                        <Link state={item} to={`/loyalty/point-based/update`}>
                          <PenLine
                            className="text-cyellow"
                              size={20}
                            />
                          </Link>
                        </span>
                      )}

                      {hasPermission("loyalty_offer.point_based_offer.delete_point_offer") && (
                      <span>
                        <Trash2
                          onClick={() => {
                            setDeleteDialog(true);
                            setSelectedId(item?.id);
                          }}
                          className="text-cred cursor-pointer"
                          size={20}
                        />
                      </span>
                      )}
                      {/* {hasPermission("loyalty_offer.point_based_offer.send_point_offer") && (
                        <Navigation
                          onClick={() => {
                            setIsAddCustomerModalOpen(true)
                            setItemsDialog(item);
                          }}
                          className="text-cwhite3 cursor-pointer"
                          size={20}
                        />
                      )} */}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex justify-start mt-4">
          <Pagination
              currentPage={currentPage} 
              totalPages={totalPages} 
              onPageChange={(e)=>dispatch(setCurrentPage(e))} 
          />
        </div>
      </div>

      <DeleteConfirmDialog
        onConfirm={() => {
          deleteHandler(selectedId);
        }}
        isOpen={deleteDialog}
        onClose={closeDeleteDialog}
      />
      <AddCustomer
        isOpen={isAddCustomerModalOpen}
        onClose={closeAddCustomer}
        title="Send Voucher via"
        options={[
          // "Whatsapp",
          // "SMS",
          "EMAIL",
          "Print",
        ]}
        value={selectedSendVoucherViaOptions}
        onChange={setSelectedSendVoucherViaOptions}
        selectedVoucher={{
          ...itemsDialog,
          restaurant_id: itemsDialog?.restaurant_id,
          point_base_card_id: itemsDialog?.id,
          reward: 1
        }}
        apiFunction={addPointBasedOfferHistoryApi}
        recallData={fetchPointBasedOffer}
        recallHistoryData={fetchPointBasedOfferHistories}
        onConfirm={handleAddCustomerConfirm}
      />

      <ViewPointBasedReward
        isOpen={isSingleVoucherModalOpen}
        onClose={closeSingleVoucher}
        selectedVoucher={itemsDialog}
      />
      <FoodItemsDialog
        items={itemsDialog}
        isOpen={ingredientsDialog}
        onClose={closeIngredients}
      />
    </TabsContent>
  );
}

export default PointBasedRewardTab;